import { Link, useLocation } from 'react-router-dom';
import { GlobeAsiaAustraliaIcon } from '@heroicons/react/24/outline';
import {
  AssetHeaderText,
  AssetName,
  Badge,
  Image,
  Tooltip,
  useGetIntegration,
} from '@plerionhq/design-system';
import type { AccessGrant } from '../../types';
import { mechanismLabels, originDisplay, scopeDisplay } from '../../label-maps';
import { TrustBadge } from '../../components/trust-badge';
import { principalToAssetPrn } from '../../lib/principal-to-prn';
import { inventoryFilterLink } from '../../lib/inventory-filter-link';

import { useTranslation } from 'react-i18next';

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  return new Date(value).toLocaleString();
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-row text-xs text-gray-500 dark:text-gray-400">
      <span className="font-semibold">{label}:</span>
      <span className="ml-1">{value}</span>
    </div>
  );
}

function IntegrationLink({ integrationId }: { integrationId: string }) {
  const { t } = useTranslation('accessGrants');
  const {
    data: integration,
    isLoading,
    isError,
  } = useGetIntegration(integrationId);
  const label = isLoading
    ? t('loading')
    : (integration?.name ??
      (isError ? t('unknownIntegration') : integrationId));
  return (
    <Tooltip
      label={
        isError ? t('integrationWithId', { integrationId }) : t('integration')
      }
    >
      <Link
        to={`/settings/integration/${integrationId}`}
        className="hyperlink max-w-[450px] overflow-hidden text-sm overflow-ellipsis"
      >
        {label}
      </Link>
    </Tooltip>
  );
}

function Lozenge({ label, badge }: { label: string; badge: React.ReactNode }) {
  return (
    <div className="flex flex-col items-start gap-1 p-2">
      <div className="flex gap-1 text-xs font-medium text-gray-600 dark:text-gray-400">
        {label}
      </div>
      {badge}
    </div>
  );
}

type AccessGrantSummaryPanelProperties = {
  grant: AccessGrant;
};

export function AccessGrantSummaryPanel({
  grant,
}: AccessGrantSummaryPanelProperties) {
  const { t } = useTranslation('accessGrants');
  const principalPrn = principalToAssetPrn(grant);
  const { search } = useLocation();
  const scope = scopeDisplay(t)[grant.grantScope];
  const origin = originDisplay(t)[grant.grantOrigin];
  const resourceName =
    grant.resourceLabel ?? grant.assetId.split(':').at(-1) ?? grant.assetId;

  return (
    <div className="rounded-panel border-container-border flex flex-col gap-4 border bg-white p-5 dark:bg-gray-800">
      <div className="flex items-start justify-between gap-6">
        <div className="flex min-w-0 items-center gap-2">
          <div className="h-[50px] w-[50px] flex-shrink-0 overflow-hidden rounded-md">
            <Image
              type="resourceType"
              name={grant.assetType}
              alt={grant.assetType}
              className="w-full object-cover"
              fallback={{
                type: 'provider',
                name: 'AWS',
                alt: 'AWS',
                className: 'w-full object-cover',
              }}
            />
          </div>
          <div className="flex min-w-0 flex-col">
            <div className="flex cursor-default flex-row flex-wrap items-center divide-x font-medium">
              <div className="pr-2">
                <IntegrationLink integrationId={grant.integrationId} />
              </div>
              <Tooltip label={t('assetAccount')}>
                <span className="px-2 text-sm text-gray-500 dark:text-gray-400">
                  {grant.resourceAccountId}
                </span>
              </Tooltip>
              <div className="px-1">
                <AssetHeaderText
                  label={t('resourceType')}
                  lg
                  text={grant.assetType}
                />
              </div>
              <div className="px-1">
                <AssetHeaderText
                  label={t('region')}
                  lg
                  icon={
                    <GlobeAsiaAustraliaIcon className="h-4 px-1 text-gray-500 dark:text-gray-400" />
                  }
                  text={grant.resourceRegion}
                />
              </div>
            </div>
            <div className="text-base font-medium text-gray-700 dark:text-gray-300">
              <AssetName id={grant.assetId} name={resourceName} />
            </div>
          </div>
        </div>
        <div className="flex flex-shrink-0 flex-col gap-0.5">
          <MetaRow
            label={t('firstObserved')}
            value={formatDate(grant.firstObservedAt)}
          />
          <MetaRow
            label={t('lastObserved')}
            value={formatDate(grant.lastObservedAt)}
          />
        </div>
      </div>
      <div className="flex flex-row text-sm text-gray-500 dark:text-gray-400">
        <span className="font-semibold">{t('grantedTo')}</span>
        <span className="ml-2 text-gray-700 dark:text-gray-300">
          {principalPrn ? (
            <AssetName id={principalPrn} name={grant.principal} />
          ) : (
            grant.principal
          )}
        </span>
      </div>
      <div className="flex flex-wrap items-start">
        <Lozenge
          label={t('scope')}
          badge={
            <Link
              to={inventoryFilterLink('grantScope', grant.grantScope, search)}
            >
              <Badge label={scope.label} status={scope.status} />
            </Link>
          }
        />
        <Lozenge
          label={t('origin')}
          badge={
            <Link
              to={inventoryFilterLink('grantOrigin', grant.grantOrigin, search)}
            >
              <Badge label={origin.label} status={origin.status} />
            </Link>
          }
        />
        <Lozenge
          label={t('common:trust')}
          badge={
            grant.trustStatus !== null ? (
              // Backend doesn't support filtering on trustStatus=null, so
              // an Unclassified link would silently navigate to "all
              // grants" — render a plain badge instead. The link still slices on
              // the coarse status, which is all the endpoint accepts.
              <Link
                to={inventoryFilterLink(
                  'trustStatus',
                  grant.trustStatus,
                  search,
                )}
              >
                <TrustBadge grant={grant} />
              </Link>
            ) : (
              <TrustBadge grant={grant} />
            )
          }
        />
        <Lozenge
          label={t('mechanism')}
          badge={
            <Link
              to={inventoryFilterLink('mechanism', grant.mechanism, search)}
            >
              <Badge
                label={mechanismLabels(t)[grant.mechanism]}
                status="info"
              />
            </Link>
          }
        />
      </div>
    </div>
  );
}
